import {Badge, Table, Tooltip} from "antd";
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import "./ResumeAbscence.css"
import dayjs from "dayjs";

const ResumeAbscence = () => {
    const { token, user } = useAuth();
    const [absenceData, setAbsenceData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch("https://api.uama.fr/v1.0/planning/absence-contrat", {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch data");
                }
                const data = await response.json();

                // Transform data based on structure
                const transformedData = transformData(data, user.Roles);
                setAbsenceData(transformedData);
            } catch (error) {
                console.error("Error fetching absence data:", error);
            }
        };

        fetchData();
    }, [token, user.Roles]);

    // Function to transform data into a consistent structure
    const transformData = (data, roles) => {
        const grouped = {};

        if (roles.includes("ROLE_PARENT") && data[0]?.["Assistante maternelle"]) {
            data.forEach(entry => {
                entry.Enfants.forEach(enfant => {
                    const key = `${enfant.Nom} - ${entry["Assistante maternelle"]}`;
                    if (!grouped[key]) {
                        grouped[key] = {
                            AssistanteMaternelle: entry["Assistante maternelle"],
                            Enfant: enfant.Nom,
                            Periodes: []
                        };
                    }
                    if (Array.isArray(enfant.Absences)) {
                        enfant.Absences.forEach(abs => {
                            grouped[key].Periodes.push({
                                debut: abs["Date début"],
                                fin: abs["Date fin"]
                            });
                        });
                    }
                });
            });
        } else {
            data.forEach(entry => {
                const key = entry.Enfant;
                if (!grouped[key]) {
                    grouped[key] = {
                        Enfant: entry.Enfant,
                        Periodes: []
                    };
                }
                entry.Absences.forEach(abs => {
                    grouped[key].Periodes.push({
                        debut: abs["Date début"],
                        fin: abs["Date fin"]
                    });
                });
            });
        }

        return Object.values(grouped);
    };

    const allColumns = [
        {
            title: "Assistante maternelle",
            dataIndex: "AssistanteMaternelle",
            key: "AssistanteMaternelle",
            render: (text) => text || "-",
        },
        {
            title: "Enfant",
            dataIndex: "Enfant",
            key: "Enfant",
        },
        {
            title: "Périodes d'absence",
            dataIndex: "Periodes",
            key: "Periodes",
            className: "cel-datetime",
            render: (periodes) => {
                const today = dayjs();

                return (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <tbody>
                        {(periodes || []).map((p, i) => {
                            const dateDebut = dayjs(p.debut, "DD/MM/YYYY");
                            const dateFin = dayjs(p.fin, "DD/MM/YYYY");
                            const isActive = today.isAfter(dateDebut.subtract(1, 'day')) && today.isBefore(dateFin.add(1, 'day'));

                            let badge = null;

                            if (isActive) {
                                if (today.isSame(dateDebut, "day")) {
                                    badge = (
                                        <Tooltip title="Début aujourd’hui">
                                            <Badge status="processing" style={{ marginLeft: 8 }} />
                                        </Tooltip>
                                    );
                                } else if (today.isSame(dateFin.subtract(1, "day"), "day")) {
                                    badge = (
                                        <Tooltip title="Fin imminente">
                                            <Badge status="warning" style={{ marginLeft: 8 }} />
                                        </Tooltip>
                                    );
                                } else {
                                    badge = (
                                        <Tooltip title="Absence en cours">
                                            <Badge status="success" style={{ marginLeft: 8 }} />
                                        </Tooltip>
                                    );
                                }
                            }

                            return (
                                <tr key={i}>
                                    <td style={{ padding: "6px 12px" }}>
                                        🗓️ {p.debut} → {p.fin}
                                        {isActive && badge}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                );
            }

        }
    ];

    // Filter columns based on role
    const columnsToShow = user.Roles.includes("ROLE_ASSISTANTE_MATERNELLE")
        ? allColumns.filter((col) => col.key !== "AssistanteMaternelle")
        : allColumns;

    return (
        <div className="ResumeAbscence">
            <Table
                columns={columnsToShow}
                dataSource={absenceData}
                pagination={{ pageSize: 3 }}
                size={"small"}
                bordered
                rowKey={(record) => `${record.Enfant}-${record["Date début"]}-${record.AssistanteMaternelle || ""}`}
                locale={{
                    emptyText: (
                        <div style={{ textAlign: 'center' }}>
                            <p style={{ color: '#888' }}>Aucune absence prévue pour le moment.</p>
                        </div>
                    ),
                }}
                rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-light' : 'table-row-dark')}
            />
        </div>
    );
};

export default ResumeAbscence;
